/**
 * screenRenderers.ts — Domain-specific screen rendering functions.
 * Each function owns one concern group from the renderScreen switch.
 */
import type { ReactNode } from "react";
import type { RenderContext } from "./renderScreen";
import type { WhisperScreen } from "@retreats/content";
import type { SheetSession } from "../screens/LiveScheduleSheet";
import { SESSION_KINDS } from "../constants";
import { TRADITION_ROWS, traditionLabel } from "../content/traditions";
import { DISCOVER_OPTIONS, ZEN_SELECTION_OPTIONS } from "./screenData";
import type { DiscoverOption } from "./screenData";
import { BELL_CATALOGUE } from "../data/bellCatalogue";
import { PushAlarmService } from "../services/PushAlarmService";
import { submitFeedback } from "../services/feedbackService";
import { track, getCurrentWave } from "../analytics/posthog";
import { deriveTeachingBells } from "../data/teachingBells";
import { displayScheduleName } from "../utils/scheduleUtils";
import { formatElapsedLabel } from "../utils/dateFormatting";
import { INTENTION_CATEGORIES, INTENTIONS, getBellPatternsForTradition, CONTAINER_TYPE_META } from "@retreats/content";
import { saveUserSchedule, deleteUserSchedule } from "../state/userScheduleRepository";
import { getAppVersion, isE2E, getTestSchedule } from "../utils/env";
import { getAllSessions } from "../state/sessionRepository";
import { RetreatState } from "./types";
import type { UpdateCta } from "../types/updates";
import { OnboardingScreen } from "../screens/OnboardingScreen";
import { PermissionsScreen } from "../screens/PermissionsScreen";
import { InstallBridgeScreen } from "../screens/InstallBridgeScreen";
import { InstallPromptScreen } from "../screens/InstallPromptScreen";
import { SelectionScreen } from "../screens/SelectionScreen";
import { LibraryScreen } from "../screens/LibraryScreen";
import { RetreatDetailScreen } from "../screens/RetreatDetailScreen";
import { SchedulingScreen } from "../screens/SchedulingScreen";
import { ScheduleWizardScreen } from "../screens/ScheduleWizardScreen";
import { WaitingScreen } from "../screens/WaitingScreen";
import { MissedScreen } from "../screens/MissedScreen";
import { HomeScreen } from "../screens/HomeScreen";
import { PreparationScreen } from "../screens/PreparationScreen";
import { IntentionScreen } from "../screens/IntentionScreen";
import { ReturningScreen } from "../screens/ReturningScreen";
import { LiveScreen } from "../screens/LiveScreen";
import { WakeUpContainerScreen } from "../screens/WakeUpContainerScreen";
import { WakeUpRitualScreen } from "../screens/WakeUpRitualScreen";
import { SingletonGateScreen } from "../screens/SingletonGateScreen";
import { EndConfirmScreen } from "../screens/EndConfirmScreen";
import { EndWithIntentionScreen } from "../screens/EndWithIntentionScreen";
import { CompletionScreen } from "../screens/CompletionScreen";
import { ReflectionScreen } from "../screens/ReflectionScreen";
import { HistoryScreen } from "../screens/HistoryScreen";
import { SessionDetailScreen } from "../screens/SessionDetailScreen";
import { SettingsScreen } from "../screens/SettingsScreen";
import { BellLibraryScreen } from "../screens/BellLibraryScreen";
import { BellPreviewScreen } from "../screens/BellPreviewScreen";
import { TrustScreen } from "../screens/TrustScreen";
import { TeachingScreen } from "../screens/TeachingScreen";
import { WhatsNewScreen } from "../screens/WhatsNewScreen";
import { AdminScreen } from "../screens/AdminScreen";
import { FeedbackScreen } from "../screens/FeedbackScreen";
import { IdlePresenceScreen } from "../screens/IdlePresenceScreen";
import { ScheduleEditorScreen } from "../screens/ScheduleEditorScreen";

const IOS_UA_RE = /iPad|iPhone|iPod/;
const EXTERNAL_LINK_TARGET = "_blank";
const EXTERNAL_LINK_REL = "noopener,noreferrer";

export type WhisperHelpers = {
  fw: (s: WhisperScreen) => string;
  fwOpt: (s: WhisperScreen) => string | null;
  fwAttr: (s: WhisperScreen) => string | undefined;
};

export type ScreenHelpers = WhisperHelpers & {
  buildDaySchedule: (
    containers: { name: string; durationSeconds: number }[],
    elapsedSeconds: number,
    currentIdx: number,
  ) => SheetSession[];
  findReflectionDraft: (endedAt: number) => string | null;
  getTemplateIdForSchedule: (scheduleId: string) => string | undefined;
};

export function renderAuthScreens(
  screen: string,
  ctx: RenderContext,
): ReactNode | null {
  const { handlers, installHandlers } = ctx;
  switch (screen) {
    case "booting":
      return (
        <div className="rc-screen rc-screen--centered">
          <div className="rc-eyebrow" style={{ opacity: 0.4 }}>
            Retreat Conductor
          </div>
        </div>
      );
    case "permissions":
      return (
        <PermissionsScreen
          onGranted={handlers.handlePermissionsGranted}
          onSkip={handlers.handlePermissionsSkip}
          onHearBell={handlers.handleHearBell}
        />
      );
    case "install-bridge":
      return (
        <InstallBridgeScreen
          onInstall={installHandlers.handleInstallBridge}
          onSkip={installHandlers.handleInstallSkip}
        />
      );
    case "install-prompt":
      return (
        <InstallPromptScreen
          isIOS={IOS_UA_RE.test(navigator.userAgent)}
          onDone={installHandlers.handleInstallDone}
          onBack={installHandlers.handleInstallBack}
        />
      );
    case "onboarding":
      return (
        <OnboardingScreen
          onBegin={() => ctx.dispatchUI({ type: "ONBOARDING_COMPLETE" })}
        />
      );
    default:
      return null;
  }
}

export function renderDiscoveryScreens(
  screen: string,
  ctx: RenderContext,
  helpers: ScreenHelpers,
): ReactNode | null {
  const { uiState, handlers, dispatchUI, preferredTradition, demoRetreat, personalSchedules } = ctx;
  switch (screen) {
    case "selection":
      return (
        <SelectionScreen
          options={ZEN_SELECTION_OPTIONS}
          onSelect={handlers.handleSelectRetreat}
          onStartNow={handlers.handleStartNow}
          onCancel={handlers.handleCancelScheduling}
          onBrowseAll={handlers.handleOpenLibrary}
        />
      );
    case "library": {
      const baseOptions = preferredTradition
        ? [
            ...DISCOVER_OPTIONS.filter((o) => o.tradition === preferredTradition),
            ...DISCOVER_OPTIONS.filter((o) => o.tradition !== preferredTradition),
          ]
        : DISCOVER_OPTIONS;
      const e2eSchedule = isE2E()
        ? getTestSchedule<import("@retreat/core").EditableRetreatSchedule>()
        : null;
      const discoverOptions: DiscoverOption[] = e2eSchedule
        ? [
            {
              schedule: e2eSchedule,
              label: e2eSchedule.name ?? "E2E Test Retreat",
              tradition: "zen" as const,
              traditionLabel: traditionLabel("zen"),
              summary: null,
              goodFor: [],
            },
            ...baseOptions,
          ]
        : baseOptions;
      return (
        <LibraryScreen
          options={discoverOptions}
          onSelect={(schedule) => {
            track("content_opened", {
              retreat_id: schedule.id,
              wave: getCurrentWave(),
            });
            const templateId = helpers.getTemplateIdForSchedule(schedule.id);
            dispatchUI({ type: "VIEW_RETREAT_DETAIL", schedule, templateId, origin: "preset", baseScheduleId: null });
          }}
          onForkSchedule={(schedule) => dispatchUI({
            type: "OPEN_SCHEDULE_EDITOR",
            editorMode: "fork",
            schedule,
          })}
          onOpenSettings={handlers.handleOpenSettings}
          onBack={handlers.handleCloseLibrary}
          initialFacet={uiState.firstLibraryOpen ? "first-timers" : undefined}
          initialTradition={uiState.libraryInitialTradition ?? undefined}
          personalSchedules={personalSchedules}
          onNewSchedule={() => {
            dispatchUI({ type: "OPEN_SCHEDULE_EDITOR", editorMode: "new" });
          }}
          onEditPersonalSchedule={(entry) => {
            dispatchUI({
              type: "OPEN_SCHEDULE_EDITOR",
              editorMode: "edit",
              schedule: entry.schedule,
              baseScheduleId: entry.baseScheduleId,
            });
          }}
          onViewPersonalSchedule={(entry) => {
            const templateId = helpers.getTemplateIdForSchedule(entry.schedule.id);
            dispatchUI({
              type: "VIEW_RETREAT_DETAIL",
              schedule: entry.schedule,
              templateId,
              origin: "owned",
              baseScheduleId: entry.baseScheduleId,
            });
          }}
          onDeletePersonalSchedule={async (id) => {
            try {
              await deleteUserSchedule(id);
              dispatchUI({ type: "USER_SCHEDULE_DELETED" });
            } catch {
              // deleteUserSchedule already reported to Sentry — leave the entry visible
              // rather than optimistically removing something that didn't actually delete.
            }
          }}
        />
      );
    }
    case "retreat-detail": {
      const viewingSchedule = uiState.viewingSchedule ?? demoRetreat;
      const viewingTemplateId = uiState.viewingTemplateId;
      return (
        <RetreatDetailScreen
          schedule={viewingSchedule}
          templateId={viewingTemplateId ?? undefined}
          bellPatterns={handlers.getRetreatBellPatterns(viewingSchedule)}
          onStartNow={(generatedSchedule) =>
            handlers.handleStartNow(generatedSchedule)
          }
          onSchedule={handlers.handleScheduleFromDetail}
          onPlaySignal={handlers.handlePlaySignal}
          onBack={() => dispatchUI({ type: "OPEN_LIBRARY" })}
          onEdit={() => dispatchUI(
            uiState.viewingOrigin === "owned"
              ? { type: "OPEN_SCHEDULE_EDITOR", editorMode: "edit", schedule: viewingSchedule, baseScheduleId: uiState.viewingBaseScheduleId }
              : { type: "OPEN_SCHEDULE_EDITOR", editorMode: "fork", schedule: viewingSchedule }
          )}
        />
      );
    }
    case "scheduling": {
      const schedulingSchedule = uiState.selectedSchedule ?? demoRetreat;
      const schedulingTemplateId = uiState.viewingTemplateId;
      return (
        <SchedulingScreen
          schedule={schedulingSchedule}
          isPushSupported={PushAlarmService.isSupported()}
          isStandalone={PushAlarmService.isStandalone()}
          onConfirm={(scheduledAt, wakeOffsetArg) => {
            if (schedulingTemplateId) {
              // Carry wakeOffset through the wizard transition — it would be lost otherwise.
              dispatchUI({ type: "GO_TO_WIZARD", scheduledAt, wakeOffset: wakeOffsetArg });
            } else {
              handlers.handleConfirmSchedule(scheduledAt, wakeOffsetArg);
            }
          }}
          onBack={handlers.handleCancelScheduling}
        />
      );
    }
    case "schedule-wizard": {
      if (!uiState.viewingTemplateId || !uiState.pendingScheduledAt) return null;
      return (
        <ScheduleWizardScreen
          templateId={uiState.viewingTemplateId}
          scheduledAt={uiState.pendingScheduledAt}
          wakeOffset={ctx.wakeOffset}
          onConfirm={(generatedSchedule) => {
            // Single call — handleConfirmSchedule dispatches CONFIRM_SCHEDULE,
            // saves to localStorage, and registers the push alarm. Calling
            // dispatchUI(CONFIRM_SCHEDULE) separately would register a second alarm.
            handlers.handleConfirmSchedule(
              uiState.pendingScheduledAt!,
              uiState.pendingWakeOffset ?? ctx.wakeOffset,
              generatedSchedule,
            );
          }}
          onBack={() => dispatchUI({ type: "CANCEL_SCHEDULING" })}
        />
      );
    }
    default:
      return null;
  }
}

export function renderScheduledRetreatScreens(
  screen: string,
  ctx: RenderContext,
  helpers: WhisperHelpers,
): ReactNode | null {
  const { uiState, handlers, dispatchUI, homeData, wave } = ctx;
  switch (screen) {
    case "waiting":
      if (!uiState.selectedSchedule || !uiState.scheduledAt) return null;
      return (
        <WaitingScreen
          schedule={uiState.selectedSchedule}
          scheduledAt={uiState.scheduledAt}
          onBeginNow={handlers.handleBeginScheduledRetreat}
          onCancel={handlers.handleCancelScheduledRetreat}
          whisperLine={helpers.fwOpt("waiting")}
        />
      );
    case "missed":
      if (!uiState.selectedSchedule || !uiState.scheduledAt) return null;
      return (
        <MissedScreen
          schedule={uiState.selectedSchedule}
          scheduledAt={uiState.scheduledAt}
          onScheduleAgain={handlers.handleCancelScheduledRetreat}
          onReturnHome={handlers.handleCancelScheduledRetreat}
        />
      );
    case "home-scheduled": {
      if (!uiState.selectedSchedule || !uiState.scheduledAt) return null;
      const requirements =
        uiState.selectedSchedule.requirements?.join(". ") ?? undefined;
      return (
        <HomeScreen
          variant="scheduled"
          todayLabel={homeData.todayLabel}
          whisperLine={helpers.fw("home-scheduled")}
          whisperAttribution={helpers.fwAttr("home-scheduled")}
          intentLabels={ctx.intentLabels}
          onOpenSettings={handlers.handleOpenSettings}
          scheduledRetreatName={displayScheduleName(uiState.selectedSchedule)}
          scheduledAt={uiState.scheduledAt}
          wakeOffset={ctx.wakeOffset}
          onChangeWakeOffset={ctx.handleChangeWakeOffset}
          onOpenTrust={handlers.handleOpenTrust}
          onBeginNow={handlers.handleBeginScheduledRetreat}
          onCancelScheduled={handlers.handleCancelScheduledRetreat}
          requirements={requirements}
          wave={wave}
          onOpenFeedback={() => dispatchUI({ type: "OPEN_FEEDBACK" })}
        />
      );
    }
    default:
      return null;
  }
}

export function renderLiveSessionScreens(
  screen: string,
  ctx: RenderContext,
  helpers: ScreenHelpers,
): ReactNode | null {
  const {
    uiState,
    handlers,
    dispatchUI,
    snapshot,
    liveData,
    activeSchedule,
    intentLabels,
  } = ctx;
  switch (screen) {
    case "preparation":
      return (
        <PreparationScreen
          onContinue={handlers.handlePreparationContinue}
          onSkip={handlers.handlePreparationContinue}
          onHearBell={handlers.handleHearBell}
          whisperFooter={helpers.fwOpt("preparation") ?? undefined}
        />
      );
    case "intention":
      return (
        <IntentionScreen
          retreatName={
            uiState.selectedSchedule
              ? displayScheduleName(uiState.selectedSchedule)
              : "Your retreat"
          }
          categories={INTENTION_CATEGORIES}
          intentions={INTENTIONS}
          onConfirm={handlers.handleIntentionComplete}
          onDecline={handlers.handleIntentionDecline}
        />
      );
    case "returning": {
      const elapsedLabel = formatElapsedLabel(snapshot.elapsedSeconds);
      const isOtherTab = uiState.otherTabActive;
      return (
        <ReturningScreen
          retreatName={
            liveData?.retreatName ??
            uiState.selectedSchedule?.name ??
            "Your retreat"
          }
          mode={isOtherTab ? "other-tab" : "resuming"}
          containerName={!isOtherTab ? liveData?.session?.name : undefined}
          elapsedLabel={!isOtherTab && elapsedLabel ? elapsedLabel : undefined}
          onReturn={
            isOtherTab
              ? handlers.handleReturningTakeover
              : handlers.handleReturningDismiss
          }
          onEnd={handlers.handleReturningEnd}
        />
      );
    }
    case "live": {
      if (
        liveData.session.kind === SESSION_KINDS.SLEEP ||
        liveData.session.kind === SESSION_KINDS.REST
      ) {
        return (
          <IdlePresenceScreen
            retreatName={
              uiState.selectedSchedule
                ? displayScheduleName(uiState.selectedSchedule)
                : "retreat"
            }
            nextBellLabel={liveData.next?.atLabel ?? ""}
            nextSessionName={liveData.next?.name ?? ""}
          />
        );
      }
      const daySchedule = helpers.buildDaySchedule(
        activeSchedule.days[0]?.containers ?? [],
        snapshot.elapsedSeconds,
        liveData.dayProgress.idx,
      );
      return (
        <LiveScreen
          session={liveData.session}
          remainingSeconds={liveData.remainingSeconds}
          elapsedSeconds={liveData.elapsedSeconds}
          totalDurationSeconds={liveData.totalDurationSeconds}
          dayProgress={liveData.dayProgress}
          endsAtLabel={liveData.endsAtLabel}
          retreatStartLabel={liveData.retreatStartLabel}
          retreatEndsLabel={liveData.retreatEndsLabel}
          intentLabels={intentLabels}
          daySchedule={daySchedule}
          whisperLine={helpers.fwOpt("mid-session") ?? undefined}
          lastBellFiredAt={ctx.lastBellFiredAt}
          onEndRetreat={ctx.handleEndGesture}
          audioSuspended={ctx.audioSuspended}
          onResumeAudio={ctx.handleResumeAudio}
        />
      );
    }
    case "wake-up-container":
      return (
        <WakeUpContainerScreen
          remaining={liveData.remaining}
          remainingFraction={liveData.remainingFraction}
          nextSessionName={liveData.next?.name ?? "the first session"}
          retreatName={
            uiState.selectedSchedule
              ? displayScheduleName(uiState.selectedSchedule)
              : activeSchedule.name ?? "retreat"
          }
        />
      );
    case "wake-ritual":
      return (
        <WakeUpRitualScreen
          intentLabels={intentLabels}
          schedule={activeSchedule}
          lastBellFiredAt={ctx.lastBellFiredAt}
          onBeginSitting={() => dispatchUI({ type: "DISMISS_WAKE_RITUAL" })}
          todayLabel={ctx.homeData.todayLabel}
          whisperLine={helpers.fwOpt("wake") ?? undefined}
        />
      );
    case "singleton-gate":
      return (
        <SingletonGateScreen
          retreatName={
            uiState.selectedSchedule
              ? displayScheduleName(uiState.selectedSchedule)
              : "Your retreat"
          }
          intentLabels={intentLabels}
          whisperLine={undefined}
          onReturn={() => dispatchUI({ type: "CLOSE_LIBRARY" })}
          onEnd={() => dispatchUI({ type: "SHOW_END_CONFIRM" })}
        />
      );
    case "end-confirm":
      return (
        <EndConfirmScreen
          onConfirm={handlers.handleEndConfirm}
          onCancel={handlers.handleEndCancel}
        />
      );
    case "end-intention": {
      const nextSessionMinutes =
        liveData.next != null
          ? Math.round(liveData.remainingSeconds / 60)
          : null;
      return (
        <EndWithIntentionScreen
          intentLabels={intentLabels}
          elapsedSeconds={snapshot.elapsedSeconds}
          nextSessionMinutes={nextSessionMinutes}
          whisperLine={helpers.fwOpt("end-intention") ?? undefined}
          onReturn={() => dispatchUI({ type: "DISMISS_END_INTENTION" })}
          onEndAnyway={handlers.handleEndConfirm}
        />
      );
    }
    default:
      return null;
  }
}

export function renderPostSessionScreens(
  screen: string,
  ctx: RenderContext,
  helpers: ScreenHelpers,
): ReactNode | null {
  const { completionData, handlers, sessions, dispatchUI } = ctx;
  switch (screen) {
    case "completion": {
      return (
        <CompletionScreen
          retreatName={completionData.retreatName}
          durationLabel={completionData.durationLabel}
          dateRangeLabel={completionData.dateRangeLabel}
          whisperText={helpers.fwOpt("completion") ?? undefined}
          sessionSaved={completionData.sessionSaved}
          onReflect={ctx.handleCompletionWithReflection}
          onClose={handlers.handleCompletionReturnHome}
          onBell={handlers.handleHearBell}
          onFeedback={() => dispatchUI({ type: "OPEN_FEEDBACK" })}
        />
      );
    }
    case "reflection": {
      const mostRecentSession = getAllSessions()[0];
      const reflectionSessionId =
        mostRecentSession?.endedAt
          ? String(mostRecentSession.endedAt)
          : String(completionData.completedAt ?? Date.now());
      return (
        <ReflectionScreen
          sessionId={reflectionSessionId}
          intentLabels={ctx.intentLabels}
          whisperLine={helpers.fwOpt("reflection")}
          date={completionData.date}
          audioReflectionEnabled={ctx.audioReflectionEnabled}
          onSave={ctx.handleSaveReflection}
          onClose={ctx.handleSkipReflection}
        />
      );
    }
    case "history":
      if (ctx.selectedHistorySession) {
        const { selectedHistorySession } = ctx;
        const endedAt =
          selectedHistorySession.endedAt ?? selectedHistorySession.startedAt;
        const sessionReflection = helpers.findReflectionDraft(endedAt);
        return (
          <SessionDetailScreen
            session={selectedHistorySession}
            reflection={sessionReflection}
            onBack={ctx.onCloseSessionDetail}
          />
        );
      }
      return (
        <HistoryScreen
          sessions={sessions}
          onBack={handlers.handleCloseHistory}
          onSelectSession={ctx.onSelectSession}
          onOpenSettings={handlers.handleOpenSettings}
        />
      );
    default:
      return null;
  }
}

export function renderSettingsScreens(
  screen: string,
  ctx: RenderContext,
): ReactNode | null {
  const { handlers, dispatchUI, defaultBellId, snapshot, uiState } = ctx;
  switch (screen) {
    case "settings": {
      const currentBellEntry = BELL_CATALOGUE.find((b) => b.id === defaultBellId);
      return (
        <SettingsScreen
          currentBellName={currentBellEntry?.name ?? defaultBellId}
          ttsEnabled={ctx.ttsEnabled}
          bellsEnabled={ctx.bellsEnabled}
          whispersEnabled={ctx.whispersEnabled}
          preferredTradition={ctx.preferredTradition}
          audioReflectionEnabled={ctx.audioReflectionEnabled}
          appVersion={getAppVersion() ?? ""}
          isRetreatActive={snapshot.status === RetreatState.RUNNING}
          isAdmin={ctx.isAdmin}
          onOpenBellLibrary={handlers.handleOpenBellLibrary}
          onToggleTTS={ctx.handleToggleTTS}
          onToggleBells={ctx.handleToggleBells}
          onToggleWhispers={ctx.handleToggleWhispers}
          onSetTradition={ctx.handleSetTradition}
          onToggleAudioReflection={ctx.handleToggleAudioReflection}
          userEmail={ctx.userEmail}
          onSignOut={ctx.signOut}
          onOpenTeaching={handlers.handleOpenTeaching}
          onOpenFeedback={() => dispatchUI({ type: "OPEN_FEEDBACK" })}
          onOpenAdmin={() => dispatchUI({ type: "OPEN_ADMIN" })}
          onOpenWhatsNew={() => dispatchUI({ type: "OPEN_WHATS_NEW" })}
          updatesUnreadCount={ctx.updatesUnreadCount}
          onBack={handlers.handleCloseSettings}
        />
      );
    }
    case "bell-library":
      return (
        <BellLibraryScreen
          defaultBellId={defaultBellId}
          onPreview={ctx.handlePreviewBell}
          onSetDefault={ctx.handleSetDefaultBell}
          onBack={handlers.handleCloseBellLibrary}
        />
      );
    case "bell-preview": {
      const bellEntry = BELL_CATALOGUE.find(
        (b) => b.id === uiState.previewingBellId,
      );
      return bellEntry ? (
        <BellPreviewScreen
          bell={bellEntry}
          isDefault={bellEntry.id === defaultBellId}
          onPlay={() => ctx.handlePreviewBell(bellEntry.id)}
          onSetDefault={() => ctx.handleSetDefaultBell(bellEntry.id)}
          onBack={handlers.handleCloseBellPreview}
        />
      ) : null;
    }
    default:
      return null;
  }
}

export function renderUtilityScreens(
  screen: string,
  ctx: RenderContext,
): ReactNode | null {
  const { handlers, dispatchUI, activeSchedule, uiState } = ctx;
  switch (screen) {
    case "trust":
      return (
        <TrustScreen
          onContinue={() => dispatchUI({ type: "CLOSE_TRUST" })}
          onBack={() => dispatchUI({ type: "CLOSE_TRUST" })}
        />
      );
    case "teaching": {
      const tradition =
        (activeSchedule.metadata?.tradition as string) ?? "Zen";
      const bells = deriveTeachingBells(
        handlers.getRetreatBellPatterns(activeSchedule),
        tradition,
      );
      return (
        <TeachingScreen
          bells={bells}
          traditionLabel={tradition}
          onBack={() => dispatchUI({ type: "CLOSE_TEACHING" })}
          onDone={() => dispatchUI({ type: "CLOSE_TEACHING" })}
          onPlayBell={(patternId) =>
            handlers.handlePlaySignal(
              patternId as import("@retreat/core").BellPatternId,
            )
          }
        />
      );
    }
    case "whats-new":
      return (
        <WhatsNewScreen
          updates={ctx.updates}
          onBack={() => dispatchUI({ type: "CLOSE_WHATS_NEW" })}
          onMount={ctx.onMarkAllRead}
          onCta={(cta: UpdateCta) => {
            dispatchUI({ type: "CLOSE_WHATS_NEW" });
            if (cta.kind === "url") {
              window.open(cta.href, EXTERNAL_LINK_TARGET, EXTERNAL_LINK_REL);
            } else if (cta.target === "library") {
              dispatchUI({ type: "OPEN_LIBRARY" });
            } else if (cta.target === "settings") {
              dispatchUI({ type: "OPEN_SETTINGS" });
            }
          }}
        />
      );
    case "admin":
      return (
        <AdminScreen onBack={() => dispatchUI({ type: "CLOSE_ADMIN" })} />
      );
    case "feedback":
      return (
        <FeedbackScreen
          userId={ctx.userId}
          wave={ctx.wave ?? "unknown"}
          onSubmit={submitFeedback}
          onClose={() => dispatchUI({ type: "CLOSE_FEEDBACK" })}
          onSubmitted={(hasAudio, hasScreenshots, hasLog) =>
            track("feedback_submitted", {
              wave: getCurrentWave(),
              has_audio: hasAudio,
              has_screenshots: hasScreenshots,
              has_log: hasLog,
            })
          }
          initialLogFile={ctx.bugLogFile ?? undefined}
        />
      );
    case "schedule-editor": {
      const editorMode = uiState.scheduleEditorMode;
      const editorSchedule = uiState.scheduleEditorSchedule;
      const editorBaseId = uiState.scheduleEditorBaseId;
      const editorTradition = editorSchedule?.tradition ?? "zen";
      const modeProps =
        editorMode === "new" || editorSchedule === null
          ? { mode: "new" as const }
          : editorMode === "fork"
            ? { mode: "fork" as const, schedule: editorSchedule, presetId: editorSchedule.id }
            : { mode: "edit" as const, schedule: editorSchedule, baseScheduleId: editorBaseId };
      return (
        <ScheduleEditorScreen
          {...modeProps}
          bellPatterns={getBellPatternsForTradition(editorTradition)}
          traditions={TRADITION_ROWS.map((t) => ({ id: t.id, name: t.name }))}
          containerTypes={CONTAINER_TYPE_META}
          saveError={uiState.scheduleEditorSaveError}
          onSave={async (entry) => {
            try {
              await saveUserSchedule(entry);
              dispatchUI({ type: "SCHEDULE_EDITOR_SAVED" });
            } catch {
              // saveUserSchedule already reported to Sentry — keep the editor open with the
              // user's edits intact instead of pretending the save succeeded.
              dispatchUI({ type: "SCHEDULE_EDITOR_SAVE_FAILED" });
            }
          }}
          onCancel={() => dispatchUI({ type: "CLOSE_SCHEDULE_EDITOR" })}
        />
      );
    }
    default:
      return null;
  }
}

export function renderHomeScreen(
  screen: string,
  ctx: RenderContext,
  helpers: WhisperHelpers,
): ReactNode | null {
  if (screen !== "home") return null;
  const { uiState, handlers, dispatchUI, homeData, wave, demo, activeSchedule, installHandlers } = ctx;

  if (uiState.isReturning || uiState.otherTabActive) {
    return (
      <HomeScreen
        variant="active"
        todayLabel={homeData.todayLabel}
        whisperLine=""
        whisperAttribution={undefined}
        intentLabels={ctx.intentLabels}
        onOpenSettings={handlers.handleOpenSettings}
        retreatName={
          uiState.otherTabRetreatName ??
          uiState.selectedSchedule?.name ??
          "Your retreat"
        }
        dayLabel="day 1"
        onReturnToPractice={ctx.handleContinueRetreat}
        onEndRetreat={handlers.handleReturningEnd}
      />
    );
  }

  return (
    <HomeScreen
      variant="idle"
      todayLabel={homeData.todayLabel}
      whisperLine={helpers.fw("home")}
      whisperAttribution={helpers.fwAttr("home")}
      intentLabels={ctx.intentLabels}
      onOpenSettings={handlers.handleOpenSettings}
      onFindPractice={handlers.handleOpenLibrary}
      onSelectTradition={(id) =>
        dispatchUI({ type: "OPEN_LIBRARY", tradition: id })
      }
      traditions={TRADITION_ROWS}
      onQuickStart={
        demo ? () => handlers.handleStartNow(activeSchedule) : undefined
      }
      onInstall={
        !PushAlarmService.isStandalone() && !installHandlers.isAppInstalled
          ? installHandlers.handleInstallBridge
          : undefined
      }
      onUpdateReady={
        installHandlers.swUpdatePending
          ? installHandlers.applySwUpdate
          : undefined
      }
      wave={wave}
      onOpenFeedback={() => dispatchUI({ type: "OPEN_FEEDBACK" })}
    />
  );
}
